import type { AudioAlignment, WordSegment } from './types';

export function stripEdgePunctuation(s: string): string {
  return s.replace(/^[\p{P}\p{S}]+|[\p{P}\p{S}]+$/gu, '');
}

// One Unicode-aware word breaker for the whole TTS pipeline. `granularity:
// 'word'` segments every language correctly — including CJK/Thai, which have no
// inter-word spaces and were previously swallowed into one giant whitespace
// token, over-highlighting a whole sentence. A single shared instance
// guarantees the alignment side (buildWordSegments) and DOM side
// (highlightWordInDom) tokenise identically, which the highlight match requires.
const wordSegmenter =
  typeof Intl !== 'undefined' && 'Segmenter' in Intl
    ? new Intl.Segmenter(undefined, { granularity: 'word' })
    : null;

/**
 * Splits `text` into word-like tokens with their start offset, using
 * `Intl.Segmenter` when available (all languages, incl. spaceless CJK/Thai) and
 * falling back to whitespace tokenisation on older engines. Edge punctuation is
 * stripped; `start` points at the first character of the stripped word.
 */
export function tokenizeWords(text: string): { word: string; start: number }[] {
  const tokens: { word: string; start: number }[] = [];

  if (!wordSegmenter) {
    const re = /\S+/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      const stripped = stripEdgePunctuation(m[0]);
      if (stripped) tokens.push({ word: stripped, start: m.index + m[0].indexOf(stripped) });
    }
    return tokens;
  }

  for (const seg of wordSegmenter.segment(text)) {
    if (!seg.isWordLike) continue;
    const stripped = stripEdgePunctuation(seg.segment);
    if (stripped) tokens.push({ word: stripped, start: seg.index + seg.segment.indexOf(stripped) });
  }
  return tokens;
}

export function buildWordSegments(alignment: AudioAlignment): WordSegment[] {
  const { characters, characterStartTimesSeconds, characterEndTimesSeconds } = alignment;

  // Timing arrays are indexed per `characters[]` entry, but tokenizeWords works
  // on the joined string. Map each string offset back to its source entry index
  // so timing stays correct even if an entry isn't a single UTF-16 code unit.
  const flatChars: string[] = [];
  const entryIndexAt: number[] = [];
  for (let i = 0; i < characters.length; i++) {
    const entry = characters[i];
    for (let k = 0; k < entry.length; k++) {
      flatChars.push(entry[k]);
      entryIndexAt.push(i);
    }
  }

  return tokenizeWords(flatChars.join('')).map(({ word, start }) => {
    const startEntry = entryIndexAt[start];
    const endEntry = entryIndexAt[start + word.length - 1] ?? startEntry;
    return {
      word,
      startTime: characterStartTimesSeconds[startEntry],
      endTime: characterEndTimesSeconds[endEntry] ?? characterStartTimesSeconds[startEntry],
      charOffset: startEntry,
    };
  });
}

// Maps each segment to its 0-based occurrence count among all segments with
// the same lowercased word. Must stay in sync with how highlightWordInDom
// counts occurrences in the DOM (both use tokenizeWords).
export function buildGlobalOccurrenceMap(segments: WordSegment[]): number[] {
  const counts: Record<string, number> = {};
  return segments.map(seg => {
    const key = seg.word.toLowerCase();
    const occ = counts[key] ?? 0;
    counts[key] = occ + 1;
    return occ;
  });
}

export interface FindCurrentWordIndexParams {
  /** Word segments to search, ordered by `startTime` ascending. */
  segments: WordSegment[];
  /** Current playback time in seconds. */
  currentTime: number;
}

/**
 * Binary-searches `segments` for the word currently being spoken at
 * `currentTime`. Returns the segment index, or `-1` when `currentTime`
 * lies between words (i.e., past the previous word's `endTime` but
 * before the next word's `startTime`).
 */
export function findCurrentWordIndex({
  segments,
  currentTime,
}: FindCurrentWordIndexParams): number {
  let lo = 0;
  let hi = segments.length - 1;
  let result = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (segments[mid].startTime <= currentTime) {
      result = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  if (result >= 0 && currentTime > segments[result].endTime) return -1;
  return result;
}

// Counts the words ElevenLabs will speak in the given TTS-ready plain text.
// Uses the same tokeniser as buildWordSegments / highlightWordInDom, so
// analytics counts match what the adapter will actually highlight.
export function countTtsWords(text: string): number {
  if (!text.trim()) return 0;
  return tokenizeWords(text).length;
}
